import React, {useEffect, useMemo, useReducer, useState} from 'react'
import PageLayout from '../../../components/layout/PageLayout'
import Text from '../../../components/text/Text'
import useNavigation from '../../../hooks/useNavigation'
import UnderLineInput from '../../../components/input/UnderLineInput'
import DateInput from '../../../components/input/DateInput'
import WizButton from '../../../components/button/WizButton'
import useQueryFloatingType from '../../../hooks/api/floating/useQueryFloatingType'
import {IFloating} from '../../../apis/floating/getFloatings'
import WizSelect from '../../../components/select/WizSelect'
import {createFloating} from '../../../apis/floating/createFloating'
import EmojiPicker from 'emoji-picker-react'

interface IProps {
    className?: string
}

const initState: IFloating = {
    title: '',
    filter: undefined,
    start_at: 0,
    end_at: 0,
    priority: 0,
    language: '',
    text: {
        ko: '',
        en: '',
    },
    emoji: '',
    action_url: {
        web: '',
        app: '',
    },
    button_text: {
        ko: '',
        en: '',
    },
    button_url: {
        web_ko: '',
        web_en: '',
        app_ko: '',
        app_en: '',
    },
}

const FloatingMessageCreatePage: React.FC<IProps> = ({className}) => {
    const {data: floatingType} = useQueryFloatingType()
    const {goFloatingMessage} = useNavigation()
    const [filterKey, setFilterKey] = useState(undefined)
    const [filterValue, setFilterValue] = useState(undefined)

    const [showPicker, setShowPicker] = useState(false)
    const [inputValue, setInputValue] = useState('')

    const handleEmojiClick = (emojiData: any) => {
        const emoji = emojiData.emoji
        dispatch({type: 'field_update', field: 'emoji', value: emoji})
        setInputValue(prev => prev + emoji)
        setShowPicker(false)
    }

    const togglePicker = () => {
        setShowPicker(prev => !prev)
    }

    const handleChange = e => {
        setInputValue(e)
    }

    const [state, dispatch] = useReducer(reducer, initState)
    const {title, start_at, emoji, end_at, filter, priority, text, language, action_url, buttonText, button_url} = state

    function reducer(state, action) {
        switch (action.type) {
            case 'field_update':
                return {...state, [action.field]: action.value}
        }
        return {...state}
    }

    const languageList = useMemo(() => {
        return [
            {id: 'ko', value: 'Korean'},
            {id: 'en', value: 'English'},
        ]
    }, [])

    const onClickFloatingMessage = async () => {
        if (
            !title ||
            !start_at ||
            !action_url?.web ||
            !action_url?.app ||
            (language === 'ko' && (!buttonText?.ko || !text?.ko)) ||
            (language === 'en' && (!buttonText?.en || !text?.en))
        ) {
            alert('Title, Start At, Text, Button Text, Action URL은 필수입니다.')
            return
        }

        const {data} = await createFloating({
            title: title,
            start_at: new Date(start_at)?.getTime() / 1000,
            end_at: end_at ? new Date(end_at)?.getTime() / 1000 : undefined,
            priority: Number(priority),
            language: language,
            text: {
                ko: text?.ko,
                en: text?.en,
            },
            filter: {
                [filterKey]: filterKey?.includes('balance') ? Number(filterValue) : filterValue,
            },
            emoji: emoji,
            action_url: {
                web: action_url?.web,
                app: action_url?.app,
            },
            button_text: {
                ko: buttonText?.ko,
                en: buttonText?.en,
            },
            button_url: {
                web_ko: button_url?.web_ko,
                web_en: button_url?.web_en,
                app_ko: button_url?.app_ko,
                app_en: button_url?.app_en,
            },
        })

        if (data?.success) {
            alert('Create Floating Message Complete')
            goFloatingMessage()
        }
    }

    const floatingOptions = useMemo(() => {
        return floatingType?.map((item, idx) => {
            return {id: item, value: item}
        })
    }, [floatingType])

    const floatingTypeValue = useMemo(() => {
        switch (filterKey) {
            case 'balance_up':
            case 'balance_down':
                return 'Balance'
            case 'league_join':
                return 'League Id'
            case 'api_key_has':
                return 'Exchange'
            default:
                return ''
        }
    }, [filterKey])

    const [viewState, setViewState] = useState(['ko', 'en'])

    const onClickChannel = (lang: string) => {
        if (viewState?.find(item => item === lang)) {
            setViewState(viewState?.filter(item => item !== lang))
        } else {
            setViewState([...viewState, lang])
        }
    }

    useEffect(() => {
        if (viewState?.length === 2 || viewState?.length === 0) {
            dispatch({type: 'field_update', field: 'language', value: ''})
        } else if (viewState?.length === 1) {
            dispatch({type: 'field_update', field: 'language', value: viewState?.at(0)})
        }
    }, [viewState])

    return (
        <PageLayout title={'Create Floating Message'}>
            <div className={'mt-[30px]'}>
                <div className={'flex justify-center gap-x-[20px]'}>
                    <div
                        onClick={() => onClickChannel('en')}
                        className={`cursor-pointer px-[40px] py-[10px] border rounded-[10px] ${
                            viewState.includes('en') && 'bg-alt_blue'
                        }`}>
                        <Text className={`${viewState.includes('en') ? 'text-white' : 'text-font03'}`}>English</Text>
                    </div>
                    <div
                        onClick={() => onClickChannel('ko')}
                        className={`cursor-pointer px-[40px] py-[10px] border rounded-[10px] ${
                            viewState.includes('ko') && 'bg-alt_blue'
                        }`}>
                        <Text className={`${viewState.includes('ko') ? 'text-white' : 'text-font03'}`}>Korean</Text>
                    </div>
                </div>
            </div>

            <div
                className={`grid max-w-[1000px] gap-x-[30px] mx-auto ${
                    viewState?.length === 2 || viewState?.length === 0 ? 'grid-cols-2' : 'grid-cols-1'
                }`}>
                {(viewState?.includes('en') || language === '') && (
                    <div
                        className={
                            'mt-[30px] w-full max-w-[500px] bg-white rounded-[10px] shadow-normal mx-auto py-[40px] px-[80px] flex flex-col gap-y-[60px] md:px-[40px] sm:w-full sm:block sm:space-y-[30px]'
                        }>
                        {language !== 'ko' && (
                            <div className={`space-y-[3px] ${language === 'en' && 'col-span-2'}`}>
                                <Text className={'text-body3 text-font02'}>Text (English)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'text',
                                            value: {...text, en: e},
                                        })
                                    }
                                />
                            </div>
                        )}

                        {language !== 'ko' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button Text (English)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'buttonText',
                                            value: {...buttonText, en: e},
                                        })
                                    }
                                />
                            </div>
                        )}

                        {language !== 'ko' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button URL (Web English)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'button_url',
                                            value: {...button_url, web_en: e},
                                        })
                                    }
                                />
                            </div>
                        )}
                        {language !== 'ko' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button URL (App English)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'button_url',
                                            value: {...button_url, app_en: e},
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}
                {(viewState?.includes('ko') || language === '') && (
                    <div
                        className={
                            'mt-[30px] w-full max-w-[500px] bg-white rounded-[10px] shadow-normal mx-auto py-[40px] px-[80px] flex flex-col gap-y-[60px]  md:px-[40px] sm:w-full sm:block sm:space-y-[30px]'
                        }>
                        {language !== 'en' && (
                            <div className={`space-y-[3px] ${language === 'ko' && 'col-span-2'}`}>
                                <Text className={'text-body3 text-font02'}>Text (Korean)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'text',
                                            value: {...text, ko: e},
                                        })
                                    }
                                />
                            </div>
                        )}

                        {language !== 'en' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button Text (Korea)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'buttonText',
                                            value: {...buttonText, ko: e},
                                        })
                                    }
                                />
                            </div>
                        )}

                        {language !== 'en' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button URL (Web Korea)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'button_url',
                                            value: {...button_url, web_ko: e},
                                        })
                                    }
                                />
                            </div>
                        )}
                        {language !== 'en' && (
                            <div className={'space-y-[3px]'}>
                                <Text className={'text-body3 text-font02'}>Button URL (App Korea)</Text>
                                <UnderLineInput
                                    onChanged={e =>
                                        dispatch({
                                            type: 'field_update',
                                            field: 'button_url',
                                            value: {...button_url, app_ko: e},
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div
                className={
                    'mt-[30px] w-full max-w-[1000px] bg-white rounded-[10px] shadow-normal mx-auto py-[40px] px-[80px] grid grid-cols-2 gap-x-[40px] gap-y-[60px] md:px-[40px] sm:w-full sm:block sm:space-y-[30px]'
                }>
                <div className={'grid grid-cols-[1fr_70px] col-span-2 gap-x-[30px]'}>
                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02'}>Title</Text>
                        <UnderLineInput onChanged={e => dispatch({type: 'field_update', field: 'title', value: e})} />
                    </div>
                    <div className={'space-y-[3px] relative'}>
                        <Text className={'text-body3 text-font02'}>Emoji</Text>
                        <UnderLineInput
                            value={inputValue}
                            onFocus={e => togglePicker()}
                            onChanged={e => handleChange(e)}
                        />
                        {showPicker && (
                            <div style={{position: 'absolute', top: '50px', zIndex: 1000}}>
                                <EmojiPicker onEmojiClick={handleEmojiClick} />
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className={
                        'grid grid-cols-[1fr_1fr_70px] sm:grid-cols-1 sm:gap-y-[30px] gap-x-[30px] col-span-2 justify-between'
                    }>
                    <div className={'space-y-[3px] justify-self-center'}>
                        <Text className={'text-body3 text-font02'}>Start At</Text>
                        <DateInput
                            onChange={e => dispatch({type: 'field_update', field: 'start_at', value: e.target.value})}
                        />
                    </div>
                    <div className={'space-y-[3px] justify-self-center'}>
                        <Text className={'text-body3 text-font02'}>End At</Text>
                        <DateInput
                            onChange={e => dispatch({type: 'field_update', field: 'end_at', value: e.target.value})}
                        />
                    </div>
                    <div className={'space-y-[3px] justify-self-center'}>
                        <Text className={'text-body3 text-font02'}>Priority</Text>
                        <UnderLineInput
                            onChanged={e => dispatch({type: 'field_update', field: 'priority', value: e})}
                        />
                    </div>
                </div>

                <div className={'flex gap-x-[10px] items-center sm:col-span-2 sm:justify-center'}>
                    <div className={'space-y-[3px]'}>
                        <Text className={'text-body3 text-font02  sm:text-center'}>Filter</Text>
                        <WizSelect
                            customOption={floatingOptions ?? undefined}
                            value={filterKey}
                            onChange={e => {
                                setFilterKey(e)
                            }}
                            className={'md:w-min md:mx-auto'}
                        />
                    </div>
                </div>
                <div className={'space-y-[3px] sm:mt-[30px] sm:col-span-2 sm:text-center'}>
                    {filterKey && (
                        <>
                            <Text className={'text-body3 text-font02'}>{floatingTypeValue}</Text>

                            {(filterKey?.includes('balance') || filterKey?.includes('league')) && (
                                <div className={'flex items-end gap-x-[5px] sm:justify-center'}>
                                    <UnderLineInput
                                        onChanged={e => {
                                            setFilterValue(e)
                                        }}
                                    />
                                    {filterKey?.includes('balance') && <Text>{'USD'}</Text>}
                                </div>
                            )}
                            {filterKey?.includes('api_key') && (
                                <WizSelect
                                    optionKey="noneDefaultExchange"
                                    value={filterValue}
                                    onChange={e => setFilterValue(e)}
                                    className={'md:w-min md:mx-auto'}
                                />
                            )}
                        </>
                    )}
                </div>

                <div className={'space-y-[3px]'}>
                    <Text className={'text-body3 text-font02'}>WEB Screen (Action URL)</Text>
                    <UnderLineInput
                        onChanged={e =>
                            dispatch({
                                type: 'field_update',
                                field: 'action_url',
                                value: {...action_url, web: e},
                            })
                        }
                    />
                </div>
                <div className={'space-y-[3px]'}>
                    <Text className={'text-body3 text-font02'}>Mobile Screen (Action URL)</Text>
                    <UnderLineInput
                        onChanged={e =>
                            dispatch({
                                type: 'field_update',
                                field: 'action_url',
                                value: {...action_url, app: e},
                            })
                        }
                    />
                </div>
            </div>

            <div className={' text-center mt-[30px]'}>
                <WizButton
                    type={'blue'}
                    size={'big'}
                    text={'Create Floating Message'}
                    onClick={() => onClickFloatingMessage()}
                />
            </div>
        </PageLayout>
    )
}

export default FloatingMessageCreatePage
